import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "../../components/ui/dropdown-menu";
import { 
  Package, BookOpen, DollarSign, Users, 
  LogOut, LayoutDashboard, ChevronRight, FileText, Gift, UserCircle, Languages, Menu, X, ChevronDown
} from "lucide-react";

// Brand Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_b31fc984-f212-4811-9faa-4930fd1aea96/artifacts/xtug1gme_image.png";

const LANG_LABELS = {
  en: "🇬🇧 English",
  es: "🇪🇸 İspanyolca",
  me: "🇲🇪 Karadağca",
  de: "🇩🇪 Almanca",
  it: "🇮🇹 İtalyanca",
  pt: "🇵🇹 Portekizce",
};

const menuItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/packages", label: "Ürünler/Paketler", icon: Package },
  { path: "/admin/bundles", label: "Kombo Paketler", icon: Gift },
  { path: "/admin/grammar-blueprint", label: "Grammar Blueprint", icon: FileText },
  { path: "/admin/lessons", label: "Dersler/Oturumlar", icon: BookOpen },
  { path: "/admin/pricing", label: "Ücret", icon: DollarSign },
  { path: "/admin/users", label: "Kullanıcılar", icon: Users },
];

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [adminLang, setAdminLang] = useState(localStorage.getItem("admin_lang") || "");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const selectLang = (lang) => {
    localStorage.setItem("admin_lang", lang);
    setAdminLang(lang);
    // Reload so the underlying admin page refetches its data scoped to the chosen language
    window.location.reload();
  };

  const switchLang = (lang) => {
    if (lang === adminLang) return;
    localStorage.setItem("admin_lang", lang);
    // Reload so every admin page refetches data scoped to the new language
    window.location.reload();
  };

  // Language selection gate — shown after admin login before any panel data loads
  if (!adminLang) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="admin-lang-gate">
        <img src={LOGO_URL} alt="Konuşuyoruz Biz" className="h-12 mb-8" />
        <h1 className="text-3xl font-bold text-white mb-2 font-['Nunito']">Hoş geldin, {user?.full_name || "Admin"} 👋</h1>
        <p className="text-neutral-400 mb-10">Hangi paneli yönetmek istersin?</p>
        <div className="flex flex-col sm:flex-row gap-6">
          <button
            onClick={() => selectLang("en")}
            data-testid="admin-lang-select-en"
            className="group w-64 h-44 rounded-2xl border border-neutral-700 bg-neutral-900 hover:border-red-600 hover:bg-neutral-800 transition-all flex flex-col items-center justify-center gap-4"
          >
            <span className="text-6xl group-hover:scale-110 transition-transform">🇬🇧</span>
            <span className="text-xl font-semibold text-white">English</span>
          </button>
          <button
            onClick={() => selectLang("es")}
            data-testid="admin-lang-select-es"
            className="group w-64 h-44 rounded-2xl border border-neutral-700 bg-neutral-900 hover:border-red-600 hover:bg-neutral-800 transition-all flex flex-col items-center justify-center gap-4"
          >
            <span className="text-6xl group-hover:scale-110 transition-transform">🇪🇸</span>
            <span className="text-xl font-semibold text-white">İspanyolca</span>
          </button>
          <button
            onClick={() => selectLang("me")}
            data-testid="admin-lang-select-me"
            className="group w-64 h-44 rounded-2xl border border-neutral-700 bg-neutral-900 hover:border-red-600 hover:bg-neutral-800 transition-all flex flex-col items-center justify-center gap-4"
          >
            <span className="text-6xl group-hover:scale-110 transition-transform">🇲🇪</span>
            <span className="text-xl font-semibold text-white">Karadağca</span>
          </button>
          <button
            onClick={() => selectLang("de")}
            data-testid="admin-lang-select-de"
            className="group w-64 h-44 rounded-2xl border border-neutral-700 bg-neutral-900 hover:border-red-600 hover:bg-neutral-800 transition-all flex flex-col items-center justify-center gap-4"
          >
            <span className="text-6xl group-hover:scale-110 transition-transform">🇩🇪</span>
            <span className="text-xl font-semibold text-white">Almanca</span>
          </button>
          <button
            onClick={() => selectLang("it")}
            data-testid="admin-lang-select-it"
            className="group w-64 h-44 rounded-2xl border border-neutral-700 bg-neutral-900 hover:border-red-600 hover:bg-neutral-800 transition-all flex flex-col items-center justify-center gap-4"
          >
            <span className="text-6xl group-hover:scale-110 transition-transform">🇮🇹</span>
            <span className="text-xl font-semibold text-white">İtalyanca</span>
          </button>
          <button
            onClick={() => selectLang("pt")}
            data-testid="admin-lang-select-pt"
            className="group w-64 h-44 rounded-2xl border border-neutral-700 bg-neutral-900 hover:border-red-600 hover:bg-neutral-800 transition-all flex flex-col items-center justify-center gap-4"
          >
            <span className="text-6xl group-hover:scale-110 transition-transform">🇵🇹</span>
            <span className="text-xl font-semibold text-white">Portekizce</span>
          </button>
        </div>
        <Button variant="ghost" className="mt-10 text-neutral-500 hover:text-white" onClick={handleLogout} data-testid="admin-gate-logout">
          <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      {/* Mobile backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="admin-drawer-backdrop"
        />
      )}

      {/* Sidebar (static on desktop, slide-in drawer on mobile) */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-neutral-900 border-r border-neutral-800 flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="p-6 pl-16 border-b border-neutral-800 relative">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Konuşuyoruz Biz" className="h-8 -ml-4" />
          </div>
          <p className="text-xs text-neutral-500 mt-2">Admin Panel</p>
          {/* Close button (mobile only) */}
          <button
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden absolute top-4 right-4 text-neutral-400 hover:text-white"
            data-testid="admin-drawer-close"
            aria-label="Menüyü kapat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setDrawerOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-red-600/20 text-red-500 font-medium' 
                        : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                    }`}
                    data-testid={`admin-nav-${item.path.split('/').pop() || 'dashboard'}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-neutral-800">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-red-600/20 text-red-500 flex items-center justify-center font-semibold text-sm">
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-neutral-400 hover:text-white hover:bg-neutral-800"
            onClick={() => navigate("/account")}
            data-testid="admin-account-btn"
          >
            <UserCircle className="w-4 h-4 mr-3" />
            Hesabım
          </Button>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-neutral-400 hover:text-white hover:bg-neutral-800"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Header */}
        <header className="bg-neutral-900 border-b border-neutral-800 px-4 sm:px-6 lg:px-8 py-4 lg:py-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden text-neutral-300 hover:text-white shrink-0"
              data-testid="admin-hamburger-btn"
              aria-label="Menüyü aç"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg lg:text-2xl font-bold text-white font-['Nunito'] truncate">{title}</h1>
          </div>

          {/* Language switcher — inline on desktop, dropdown on mobile */}
          <div className="hidden lg:flex items-center gap-2" data-testid="admin-lang-switcher">
            <Languages className="w-4 h-4 text-neutral-500" />
            {Object.keys(LANG_LABELS).map((lang) => (
              <button
                key={lang}
                onClick={() => switchLang(lang)}
                data-testid={`admin-lang-switch-${lang}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  adminLang === lang
                    ? 'bg-red-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                {LANG_LABELS[lang]}
              </button>
            ))}
          </div>
          {/* Mobile language dropdown */}
          <div className="lg:hidden shrink-0" data-testid="admin-lang-switcher-mobile">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-neutral-800 text-white"
                  data-testid="admin-lang-mobile-trigger"
                >
                  <Languages className="w-4 h-4 text-neutral-400" />
                  <span>{LANG_LABELS[adminLang] || "Dil"}</span>
                  <ChevronDown className="w-4 h-4 text-neutral-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-neutral-900 border-neutral-700">
                {Object.keys(LANG_LABELS).map((lang) => (
                  <DropdownMenuItem
                    key={lang}
                    onClick={() => switchLang(lang)}
                    data-testid={`admin-lang-switch-mobile-${lang}`}
                    className={`cursor-pointer ${adminLang === lang ? "text-red-500 font-medium" : "text-neutral-300"}`}
                  >
                    {LANG_LABELS[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
